import React from 'react';
import styled from 'styled-components';

import { default as MenuItemLink } from 'components/molecules/item-link';
import { Translate } from 'core/i18n/types';

type Permissions = {
  canReadHelpInfo: boolean;
  canReadSituations: boolean;
  canReadReasons: boolean;
  canReadTypologies: boolean;
  canReadProfiles: boolean;
};

export type MenuKey =
  | 'circulation'
  | 'historisation'
  | 'customers'
  | 'customers/OD'
  | 'customers/station'
  | 'administration'
  | 'administration/referentiel'
  | 'administration/referentiel/help-infos'
  | 'help-infos'
  | 'compensation-sentences'
  | 'administration/access'
  | 'profils'
  | 'users'
  | 'administration/referentiel/situations'
  | 'situations'
  | 'constitution'
  | 'masks'
  | 'reasons'
  | 'administration/referentiel/typologies'
  | 'product-codes'
  | 'typologies';

export type MenuItemConfig = {
  key: MenuKey;
  label: React.ReactNode;
  children?: MenuItemConfig[];
};

const SubMenuWrapper = styled.span`
  text-transform: uppercase;
`;

export const circulationItems = (t: ReturnType<Translate>): MenuItemConfig => {
  return {
    key: 'circulation',
    label: <MenuItemLink to="/circulation" name={t('menu', 'MENU_ITEM_1')} />,
  };
};

export const historisationItems = (t: ReturnType<Translate>): MenuItemConfig => {
  return {
    key: 'historisation',
    label: <MenuItemLink to="/historisation" name={t('menu', 'MENU_ITEM_4')} />,
  };
};

export const customersItems = (t: ReturnType<Translate>): MenuItemConfig => {
  return {
    key: 'customers',
    label: <SubMenuWrapper>{t('menu', 'MENU_ITEM_2')}</SubMenuWrapper>,
    children: [
      {
        key: 'customers/OD',
        label: (
          <SubMenuWrapper>
            <MenuItemLink to="/customers/OD" name={t('menu', 'MENU_ITEM_4_1')} />
          </SubMenuWrapper>
        ),
      },
      {
        key: 'customers/station',
        label: (
          <SubMenuWrapper>
            <MenuItemLink to="/customers/station" name={t('menu', 'MENU_ITEM_4_2')} />
          </SubMenuWrapper>
        ),
      },
    ],
  };
};

export const administrationItems = (
  t: ReturnType<Translate>,
  { canReadHelpInfo, canReadSituations, canReadReasons, canReadTypologies, canReadProfiles }: Permissions
): MenuItemConfig => {
  const adminReferentielHelpInfosItems: MenuItemConfig = {
    key: 'administration/referentiel/help-infos',
    label: <SubMenuWrapper>{t('menu', 'MENU_ITEM_3_1_1')}</SubMenuWrapper>,
    children: [
      {
        key: 'help-infos',
        label: (
          <SubMenuWrapper>
            <MenuItemLink to="/help-infos" name={t('menu', 'MENU_ITEM_3_1_1_1')} />
          </SubMenuWrapper>
        ),
      },
      {
        key: 'compensation-sentences',
        label: (
          <SubMenuWrapper>
            <MenuItemLink to="/compensation-sentences" name={t('menu', 'MENU_ITEM_3_1_1_2')} />
          </SubMenuWrapper>
        ),
      },
    ],
  };
  const adminReferentielSituationsItems: MenuItemConfig = {
    key: 'administration/referentiel/situations',
    label: <SubMenuWrapper>{t('menu', 'MENU_ITEM_3_1_3')}</SubMenuWrapper>,
    children: [
      {
        key: 'situations',
        label: (
          <SubMenuWrapper>
            <MenuItemLink to="/situations" name={t('menu', 'MENU_ITEM_3_1_3_1')} />
          </SubMenuWrapper>
        ),
      },
      {
        key: 'constitution',
        label: (
          <SubMenuWrapper>
            <MenuItemLink to="/constitution" name={t('menu', 'MENU_ITEM_3_1_3_3')} />
          </SubMenuWrapper>
        ),
      },
      {
        key: 'masks',
        label: (
          <SubMenuWrapper>
            <MenuItemLink to="/masks" name={t('menu', 'MENU_ITEM_3_1_3_2')} />
          </SubMenuWrapper>
        ),
      },
    ],
  };
  const adminReferentielReasonsItems: MenuItemConfig = {
    key: 'reasons',
    label: (
      <SubMenuWrapper>
        <MenuItemLink to="/reasons" name={t('menu', 'MENU_ITEM_3_1_2')} />
      </SubMenuWrapper>
    ),
  };
  const adminReferentielTypologiesItems: MenuItemConfig = {
    key: 'administration/referentiel/typologies',
    label: <SubMenuWrapper>{t('menu', 'MENU_ITEM_3_1_4')}</SubMenuWrapper>,
    children: [
      {
        key: 'product-codes',
        label: (
          <SubMenuWrapper>
            <MenuItemLink to="/product-codes" name={t('menu', 'MENU_ITEM_3_1_4_1')} />
          </SubMenuWrapper>
        ),
      },
      {
        key: 'typologies',
        label: (
          <SubMenuWrapper>
            <MenuItemLink to="/typologies" name={t('menu', 'MENU_ITEM_3_1_4_2')} />
          </SubMenuWrapper>
        ),
      },
    ],
  };
  const adminAccessProfilsItems: MenuItemConfig = {
    key: 'profils',
    label: (
      <SubMenuWrapper>
        <MenuItemLink to="/profils" name={t('menu', 'MENU_ITEM_3_3_3')} />
      </SubMenuWrapper>
    ),
  };
  const adminAccessUsersItems: MenuItemConfig = {
    key: 'users',
    label: (
      <SubMenuWrapper>
        <MenuItemLink to="/users" name={t('menu', 'MENU_ITEM_3_2')} />
      </SubMenuWrapper>
    ),
  };

  const adminReferentielChildren = [];
  if (canReadHelpInfo) {
    adminReferentielChildren.push(adminReferentielHelpInfosItems);
  }
  if (canReadSituations) {
    adminReferentielChildren.push(adminReferentielSituationsItems);
  }
  if (canReadReasons) {
    adminReferentielChildren.push(adminReferentielReasonsItems);
  }
  if (canReadTypologies) {
    adminReferentielChildren.push(adminReferentielTypologiesItems);
  }

  const adminAccessChildren = [];
  if (canReadProfiles) {
    adminAccessChildren.push(adminAccessProfilsItems);
  }
  adminAccessChildren.push(adminAccessUsersItems);

  const adminReferentielItems: MenuItemConfig = {
    key: 'administration/referentiel',
    label: <SubMenuWrapper>{t('menu', 'MENU_ITEM_3_1')}</SubMenuWrapper>,
    children: adminReferentielChildren,
  };
  const adminAccessItems: MenuItemConfig = {
    key: 'administration/access',
    label: <SubMenuWrapper>{t('menu', 'MENU_ITEM_3_3')}</SubMenuWrapper>,
    children: adminAccessChildren,
  };

  return {
    key: 'administration',
    label: <SubMenuWrapper>{t('menu', 'MENU_ITEM_3')}</SubMenuWrapper>,
    children: [{ ...adminReferentielItems }, { ...adminAccessItems }],
  };
};
